import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Shield, Trash2 } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { extractErrorMessage } from '@/shared/api/errors';
import { useRolesList } from '@/entities/roles';
import { usePerm } from '@/shared/hooks/usePerm';
import { RoleDeleteDialog } from '@/features/roles';
import { formatDate } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';
import type { ApiRole } from '@/shared/types/api/roles';

const PAGE_SIZE = 20;

export const WidgetsRolesMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = usePerm('roles.create');
  const canEdit = usePerm('roles.edit');
  const canDelete = usePerm('roles.delete');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<ApiRole | null>(null);

  const query = useRolesList();
  const all = query.data ?? [];
  const total = all.length;
  const pageStart = (page - 1) * PAGE_SIZE;
  const rows = all.slice(pageStart, pageStart + PAGE_SIZE);

  const columns = useMemo<ColumnDef<ApiRole>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('roles.list.columns.name'),
        cell: (info) => (
          <span className="font-medium text-ink">{info.row.original.name}</span>
        ),
      },
      {
        accessorKey: 'is_super',
        header: t('roles.list.columns.kind'),
        cell: (info) =>
          info.row.original.is_super ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                'border-indigo-200 bg-indigo-100 text-indigo-800',
              )}
            >
              <Shield className="h-3 w-3" />
              {t('roles.list.superBadge')}
            </span>
          ) : (
            <span className="text-xs text-ink-muted">
              {t('roles.list.customBadge')}
            </span>
          ),
      },
      {
        accessorKey: 'permissions',
        header: t('roles.list.columns.permissions'),
        cell: (info) => {
          const r = info.row.original;
          if (r.is_super) return <span className="text-ink-muted">∞</span>;
          return (
            <span className="font-mono text-xs">{r.permissions.length}</span>
          );
        },
      },
      {
        accessorKey: 'staff_count',
        header: t('roles.list.columns.staffCount'),
        cell: (info) => (
          <span className="font-mono text-xs">
            {info.row.original.staff_count ?? 0}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: t('roles.list.columns.createdAt'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('roles.list.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className="flex items-center gap-1.5">
              {canEdit && (
                <IconButton
                  label={t('common.edit')}
                  onClick={() => navigate(`/roles/${r.id}/edit`)}
                >
                  <Pencil className="h-4 w-4" />
                </IconButton>
              )}
              {canDelete && (
                <IconButton
                  label={t('common.delete')}
                  variant="danger"
                  onClick={() => setDeleteTarget(r)}
                  disabled={r.is_super}
                >
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              )}
            </div>
          );
        },
      },
    ],
    [t, canEdit, canDelete, navigate],
  );

  return (
    <>
      <PageHeader
        title={t('roles.list.title')}
        subtitle={t('roles.list.subtitle')}
        right={
          canCreate ? (
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/roles/new')}
              title={t('roles.list.newRole')}
              aria-label={t('roles.list.newRole')}
            >
              <span className="hidden sm:inline">{t('roles.list.newRole')}</span>
            </Button>
          ) : null
        }
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('roles.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <DataTable
              data={rows}
              columns={columns}
              isLoading={query.isLoading}
              emptyTitle={t('roles.list.emptyTitle')}
              emptyIcon={<Shield className="h-8 w-8" />}
              getRowId={(row) => row.id}
              numbered
              pageIndex={page - 1}
              pageSize={PAGE_SIZE}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      <RoleDeleteDialog
        open={Boolean(deleteTarget)}
        role={deleteTarget}
        allRoles={all}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
};
